import { z } from 'zod';
import type { HeldRecord } from './records';

const objectId = z.string().regex(/^[a-f0-9]{24}$/, 'Must be an ID');

/**
 * What a dispense_notice carries: one dispensing record, and the signed
 * prescription it answers. The prescription lets the patient's phone find its
 * own copy, and lets the doctor's device and the phone check that the pharmacy
 * really holds a prescription the doctor signed for this patient.
 */
export const dispenseNoticePayloadSchema = z.strictObject({
  v: z.literal(1),
  /** The practice that dispensed: a pharmacy, or a clinic with its own counter. */
  dispensedBy: objectId,
  prescription: z.strictObject({
    /** The practice that wrote it and its own record ID, as the patient's copy was keyed. */
    practiceId: objectId,
    recordId: z.uuid(),
    resource: z.record(z.string(), z.unknown()),
  }),
  dispense: z.strictObject({
    id: z.uuid(),
    version: z.number().int().min(1),
    resource: z.record(z.string(), z.unknown()),
  }),
});
export type DispenseNoticePayload = z.infer<typeof dispenseNoticePayloadSchema>;

/**
 * How far a record holder has sent notices: the store's change counter up to
 * which every dispensing record has gone to its patient and doctor. Kept
 * encrypted. Losing it only makes the holder send notices again, which the
 * receivers recognise.
 */
export interface NoticeCursor {
  get(): Promise<number>;
  set(seq: number): Promise<void>;
}

/** The notice for a dispensing record and the prescription it answers, as the holder that keeps both sends it. */
export function noticeOf(dispense: HeldRecord, prescription: HeldRecord, dispensedBy: string): DispenseNoticePayload {
  return {
    v: 1,
    dispensedBy,
    prescription: {
      // A copy names its source. The practice's own prescription, at a clinic counter, is its own.
      practiceId: prescription.source?.practiceId ?? dispensedBy,
      recordId: prescription.source?.recordId ?? prescription.id,
      resource: prescription.resource,
    },
    dispense: { id: dispense.id, version: dispense.version, resource: dispense.resource },
  };
}
